import {
  normalizeSettings,
  REPORT_REASONS,
  REPORT_STATUSES,
  ROLES,
  validateFamiliar,
} from "@hexling/core";
import { z } from "zod";
import { contentCategory, slug } from "./content";

const iso = z.iso.datetime({ offset: true });

/** Fields every synced record carries (see core/sync.ts). */
const syncFields = {
  id: z.uuid(),
  updatedAt: iso,
  deletedAt: iso.nullable(),
};

/** A reference from user data to a content item. */
export const contentRef = z.object({ category: contentCategory, slug });

export const note = z.object({
  ...syncFields,
  title: z.string().trim().min(1).max(200),
  body: z.string().max(50_000),
  tags: z.array(z.string().trim().min(1).max(40)).max(30),
  refs: z.array(contentRef).max(50),
  /** Optional link to a moon phase or date the note is about, e.g. a ritual journal. */
  occurredOn: z.iso.date().nullable(),
});
export type Note = z.infer<typeof note>;

export const list = z.object({
  ...syncFields,
  name: z.string().trim().min(1).max(100),
  description: z.string().max(1000),
  icon: z.string().max(16),
});
export type List = z.infer<typeof list>;

export const listItem = z.object({
  ...syncFields,
  listId: z.uuid(),
  ref: contentRef,
  note: z.string().max(2000),
  position: z.number().int().min(0),
});
export type ListItem = z.infer<typeof listItem>;

export const bookmark = z.object({
  ...syncFields,
  ref: contentRef,
});
export type Bookmark = z.infer<typeof bookmark>;

/** Settings are always normalized, so any shape in is a valid shape out. */
export const settings = z.unknown().transform((v) => normalizeSettings(v));

export const familiar = z
  .object({
    typeSlug: z.string().min(1).max(60),
    customType: z.string().max(40).nullable().optional(),
    name: z.string().max(40),
  })
  .superRefine((f, ctx) => {
    for (const message of validateFamiliar(f)) ctx.addIssue({ code: "custom", message });
  });
export type FamiliarInput = z.infer<typeof familiar>;

export const profile = z.object({
  updatedAt: iso,
  settings,
  familiar: familiar.nullable(),
});

const MAX_BATCH = 500;

export const syncPush = z.object({
  notes: z.array(note).max(MAX_BATCH).default([]),
  lists: z.array(list).max(MAX_BATCH).default([]),
  listItems: z.array(listItem).max(MAX_BATCH).default([]),
  bookmarks: z.array(bookmark).max(MAX_BATCH).default([]),
  profile: profile.optional(),
});
export type SyncPush = z.infer<typeof syncPush>;

export const syncPullQuery = z.object({
  cursor: iso.optional(),
});

export interface SyncPullResponse {
  notes: Note[];
  lists: List[];
  listItems: ListItem[];
  bookmarks: Bookmark[];
  profile: {
    updatedAt: string;
    settings: ReturnType<typeof normalizeSettings>;
    familiar: FamiliarInput | null;
  } | null;
  /** Pass back as `cursor` next time. */
  cursor: string;
}

export const reportInput = z.object({
  category: contentCategory,
  slug,
  reason: z.enum(REPORT_REASONS),
  message: z
    .string()
    .trim()
    .min(10, "Please tell us a little more (at least 10 characters).")
    .max(2000),
});
export type ReportInput = z.infer<typeof reportInput>;

export const reportUpdate = z.object({
  status: z.enum(REPORT_STATUSES),
  resolutionNote: z.string().max(2000).optional(),
});

export const setRole = z.object({ role: z.enum(ROLES) });
